import type { TestStep } from '../api/tests';

/**
 * "id | what to do | what should be seen | ledger row | good answer | url", one step
 * per line; the id is made from the line's number when left out, and the good answer
 * ("sí" or "no") is "sí" when left out: a "no" to a step whose good answer is "no"
 * files no report. The url is a web card's: the path of this web app where the step
 * starts ("/library"); anything that is not such a path is left out here, and the
 * platform refuses it.
 */
export function parseSteps(text: string): TestStep[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      const parts = line.split('|').map((p) => p.trim());
      const explicitId = parts.length >= 2 && /^[a-z0-9_]{1,32}$/.test(parts[0]);
      const id = explicitId ? parts[0] : `s${i + 1}`;
      const rest = explicitId ? parts.slice(1) : parts;
      const step: TestStep = { id, text: rest[0] ?? '' };
      if (rest[1]) step.expect = rest[1];
      if (rest[2]) step.ledgerRow = rest[2].toUpperCase();
      if (rest[3] && rest[3].toLowerCase() === 'no') step.goodAnswer = 'no';
      if (rest[4] && isAppPath(rest[4])) step.url = rest[4];
      return step;
    });
}

/** A path of this web app and nothing else: one leading slash, no scheme, no host, no blank. */
export function isAppPath(url: string | null | undefined): url is string {
  return (
    !!url &&
    url.startsWith('/') &&
    !url.startsWith('//') &&
    !url.includes('\\') &&
    !url.includes('://') &&
    !/\s/.test(url)
  );
}

/** An id no step of the card has yet, for a step written without one. */
export function freeStepId(steps: TestStep[]): string {
  const taken = new Set(steps.map((s) => s.id));
  let n = steps.length + 1;
  while (taken.has(`s${n}`)) n += 1;
  return `s${n}`;
}
